<template>
    <div class="dashboard view-container">
        <div class="view-content">
            <div id="top-row" class="top-row">
                <!-- CONSIDERA TODOS OS USUÁRIOS QUANDO FAMILY -->
                <transaction-card
                    type="balance"
                    :totalValue="balances.currentBalance"
                    :budgetValue="balancePreviousMonths"
                    :remainValue="balanceTotalMonth(undefined)"
                    :switchingEye="true"
                    :hide="hideValues"
                    :tooltip-data="tooltips.total"
                    @hide-info="hideCardValues"
                />

                <transaction-card
                    id="revenues-card"
                    type="revenues"
                    :totalValue="balanceMonthExpectedPerType('revenues')"
                    :budgetValue="balanceMonthPerType('revenues', true)"
                    :remainValue="balanceMonthPerType('revenues', false)"
                    :hide="hideValues"
                    :tooltip-data="tooltips.revenues"
                />

                <transaction-card
                    type="expenses"
                    :totalValue="balanceMonthExpectedPerType('expenses')"
                    :budgetValue="balanceMonthPerType('expenses', true)"
                    :remainValue="balanceMonthPerType('expenses', false)"
                    :hide="hideValues"
                    :tooltip-data="tooltips.expenses"
                />

                <transaction-card
                    type="investments"
                    :totalValue="savingsCardData.paid"
                    :budgetValue="savingsCardData.planned"
                    :remainValue="0"
                    :hide="hideValues"
                    :tooltip-data="tooltips.savings"
                    :loading="loadings.savingCard"
                />
            </div>
            <!-- /.top-row -->

            <div class="main-content-row">
                <div class="left-col flex">
                    <chart-box
                        style="min-width: 650px"
                        class="card-mt"
                        boxTitle="Receitas x Despesas (Diário)"
                        chart="LineChart"
                        :chartSeries="daylyLineChart"
                        :chartOptions="getDays()"
                        :loading="loadings.daylyChart"
                    />

                    <chart-box
                        style="height: 250px"
                        class="card-mt"
                        :width="'240'"
                        boxTitle="Gastos por categoria"
                        chart="CategoryChart"
                        :chartSeries="categories.chartSeries"
                        :chartOptions="categories.chartOptions"
                        :chartProps="{
                            ...this.donutDimensions,
                            categoriesID: categories.chartCategoriesID,
                            modalEvent: true,
                        }"
                        :modalEvent="modalEvent"
                    />

                    <chart-box
                        style="height: 250px"
                        boxTitle="Consumo variável"
                        chart="VariableSpending"
                        :chartSeries="budget.chartSeries"
                        :chartOptions="budget.chartOptions"
                        :chartProps="{ ...this.donutDimensions }"
                        :modalEvent="false"
                    />
                </div>

                <div class="center-col flex">
                    <dashboard-list
                        class="card-mt next-transactions"
                        cardTitle="próximos pagamentos"
                        transactionType="expenses"
                        :list="nextExpenses"
                        :user="user"
                    />

                    <accounts-box style="flex: 1; height: 350px" :userPayments="payments" />

                    <dashboard-list
                        v-if="!$variables.hideScreens.hideScreenTasks"
                        class="tasks-list"
                        cardTitle="Suas tarefas"
                        list-type="tasks"
                        :list="userTasks.reverse().slice(0, 5)"
                        empty-text="Sem tarefas registradas"
                        :user="user"
                    />
                </div>
            </div>
            <!-- /.main-content-row -->
        </div>
        <!-- /.content -->

        <Onboarding
            v-if="!user.skipTutorial && !waiting && !firstLoginModalOpen"
            @scroll-component="scrollToComponent"
        />

        <FirstLogin v-show="firstLoginModalOpen" @close-modal="firstLoginModalOpen = false" />
    </div>
    <!-- /.container -->
</template>

<script>
import { mapState } from 'pinia'
import { mainStore } from '../store'

import Onboarding from '../components/Onboarding.vue'
import TransactionCard from '../components/cards/TransactionCard.vue'
import DashboardList from '../components/lists/DashboardList.vue'
import ChartBox from '../components/cards/ChartBox.vue'
import AccountsBox from '../components/cards/AccountsBox.vue'
import FirstLogin from '../components/modals/FirstLogin.vue'
import ChatWindow from '../components/ChatWindow'
import ActionMenu from '../components/ActionMenu'

import authService from '../services/auth.service'
import formatDate from '../common/formatDate'
import apiService from '../services/api.service'
import { environment } from '../common/environment'

const tooltips = {
    total: {
        'Saldo atual': 'Somatório de todas as contas bancárias e dinheiro.',
        'Saldo prévio': 'Saldo atual - Saldo do mês.',
        'Saldo do mês': 'Balanço previsto das movimentações do mês atual.',
    },
    revenues: {
        Receitas: 'Somatório de todas as receitas.',
        Recebidas: 'Balanço de todas as receitas recebidadas.',
        'A receber': 'Balanço de todas as receitas não-recebidas.',
    },
    expenses: {
        Despesas: 'Somatório de todas as despesas.',
        Pagas: 'Balanço de todas as despesas pagas.',
        'A pagar': 'Balanço de todas as despesas não-pagas.',
    },
    savings: {
        Poupança: 'Balanço das transações de investimento da categoria poupança efetivadas',
        Programado: 'Total de todas transações de investimento da categoria poupança já regitradas até o mês atual.',
    },
}

export default {
    name: 'Dashboard',

    components: {
        Onboarding,
        TransactionCard,
        DashboardList,
        ChartBox,
        AccountsBox,
        ChatWindow,
        ActionMenu,
        FirstLogin,
    },

    data() {
        return {
            objectives: {},
            categories: {
                chartSeries: [],
                chartOptions: {
                    plotOptions: {
                        pie: {
                            offsetY: 5,
                            offsetX: 0,
                        },
                    },
                },
                chartCategoriesID: [],
            },
            budget: {
                chartOptions: {},
                chartSeries: [],
            },
            donutDimensions: { height: 180 },

            modalEvent: true,

            hideValues: !!authService.getOnLocalStorage('hideDashboardCardValues'),

            loadings: {
                daylyChart: true,
                savingCard: true,
            },

            savingsCardData: {
                paid: 0,
                planned: 0,
            },

            tooltips,

            firstLoginModalOpen: false,
        }
    },

    methods: {
        hideCardValues() {
            if (!this.hideValues) {
                authService.saveOnLocalStorage('hideDashboardCardValues', 'hide')
                this.hideValues = true
                return
            }

            authService.removeFromLocalStorage('hideDashboardCardValues')
            this.hideValues = false
            return
        },

        getDays() {
            let chartOptions = {
                xaxis: {
                    categories: formatDate.getDays(this._date.inicio),
                },
            }
            return chartOptions
        },

        attExpensesChart() {
            let options = this.categoriesChartOptions(
                'expenses',
                undefined,
                this.plannerWithFamily ? this.mainUserId : undefined,
            )

            if (options.chartSeries.length < 1) {
                this.categories.chartSeries = [1]
                this.categories.chartOptions = {
                    labels: ['Sem gastos'],
                    colors: ['#DCDCDC'],
                    states: {
                        hover: {
                            filter: {
                                type: 'none',
                            },
                        },
                    },
                    plotOptions: {
                        pie: {
                            expandOnClick: false,
                        },
                    },
                    tooltip: {
                        enabled: false,
                    },
                }
            } else {
                this.categories.chartSeries = options.chartSeries
                this.categories.chartOptions = Object.assign(this.categories.chartOptions, options.chartOptions)
                this.categories.chartCategoriesID = [...options.categories_id]
                this.modalEvent = true
            }
        },

        attBudgetChart() {
            if (!this.budgets._id) {
                ;(this.budget.chartSeries = [1]),
                    (this.budget.chartOptions = {
                        labels: ['Sem orçamento'],
                        colors: ['#DCDCDC'],
                        states: {
                            hover: {
                                filter: {
                                    type: 'none',
                                },
                            },
                        },
                        plotOptions: {
                            pie: {
                                offsetY: 5,
                                offsetX: 0,
                                expandOnClick: false,
                            },
                        },
                        tooltip: {
                            enabled: false,
                        },
                    })
            } else {
                ;(this.budget.chartOptions = {
                    labels: [
                        'Despesas fixas',
                        'Despesas variáveis',
                        'Dividas & parcelas',
                        'Investimentos',
                        'Não-reservado',
                    ],
                    colors: this.planningChartColors,
                    plotOptions: {
                        pie: {
                            offsetY: 5,
                            offsetX: 0,
                        },
                    },
                }),
                    (this.budget.chartSeries = this.planningChartSeries)
            }
        },

        async getSavingsBalance() {
            try {
                const savingsCategoryId = this.filterBy('categories', {
                    user: this.mainUserId,
                    group: 'investiments',
                    name: 'Poupança',
                })[0]?._id

                const response = await apiService.get(
                    `${environment.BALANCES_URL}?userId=${this.mainUserId}&categoryId=${savingsCategoryId}&startDate=${this._date.inicio}&endDate=${this._date.fim}`,
                )

                const plannedSavings =
                    this.budgets?.investimentCategories?.find((cB) => cB.category._id === savingsCategoryId)?.value || 0

                this.savingsCardData.paid = Math.abs(response.data.expensesPaid)
                this.savingsCardData.planned = Math.abs(response.data.expensesBalance)
            } catch (error) {
                console.error(error)
            } finally {
                this.loadings.savingCard = false
            }
        },

        scrollToComponent(id) {
            let pos = 0

            switch (id) {
                case 'revenues-card':
                    const element = document.getElementById(id)
                    element.scrollIntoView({ behavior: 'smooth', block: 'end' })
                    break
            }
        },
    },

    watch: {
        transactions() {
            this.attExpensesChart()
        },

        planningIncomes() {
            this.attBudgetChart()
        },

        waiting: {
            handler(value) {
                if (!value && this.loadings.savingCard) this.getSavingsBalance()
            },
        },
    },

    computed: {
        ...mapState(mainStore, [
            'balances',
            'balancesChart',
            '_date',
            'payments',
            'transactions',
            'user',
            'waiting',
            'budgets',
            'balancePreviousMonths',
            'balanceMonthExpectedPerType',
            'balancePaymentsPerType',
            'balanceMonthPerType',
            'balanceInvestments',
            'balanceTotalMonth',
            'nextExpenses',
            'categoriesChartOptions',
            'daylyLineChart',
            'planningChartColors',
            'planningIncomes',
            'planningChartSeries',
            'userTasks',
            'mainUserId',
            'plannerWithFamily',
            'filterBy',
        ]),

        balance() {
            return this.balanceMonthExpectedPerType('revenues', null)
        },
    },

    mounted() {
        this.attExpensesChart()
        this.attBudgetChart()

        this.loadings.daylyChart = false

        if (!this.waiting) {
            this.getSavingsBalance()
            this.firstLoginModalOpen = !this.user.skipTutorial && this.$variables.tutorial.disableBasicTutorial
        }
    },
}
</script>

<style lang="scss" scoped>
.flex-between {
    @include flex-between();
}

.card-mt {
    margin-right: 24px;
}

.next-transactions {
    width: 290px;
    max-height: 350px;
}

.tasks-list {
    margin-left: 24px;
    width: 260px;
    max-height: 350px;
}

.top-row {
    padding-bottom: 24px;
    display: flex;
    justify-content: space-between;
    border-bottom: solid 1px $lighter-grey;
    width: 100%;
}

.main-content-row {
    display: flex;
    flex-direction: column;
    padding: 24px 0;

    .left-col {
        margin-bottom: 24px;
    }

    .right-col {
        width: 340px;
        position: relative;
    }
}

.flex {
    display: flex;
}

.grow-1 {
    flex-grow: 1;
}

.grow-2 {
    flex: 1;
}

.banner {
    width: 354px;
    padding: 20px;
    background: $deep-blue;
    text-align: center;

    h3 {
        margin-bottom: 15px;
        font-weight: bold;
        font-size: 20px;
        color: #ffffff;
    }

    p {
        margin-bottom: 15px;
        font-size: 16px;
        line-height: 1.2;
        color: #ffffff;
    }

    .cta-btn {
        padding: 10px 20px;
        background: $revenue-green;
        border-radius: 67px;
        font-weight: 500;
        font-size: 16px;
        color: #ffffff;
    }
}

.flex-expand {
    flex: 1;
}
</style>
